import { useRef, useState } from 'react';
import { C } from '../../lib/tokens.js';

export function IconText({ icon, children, color = C.iron, size = 13 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color, fontSize: size }}>
      <span className="material-icons" style={{ fontSize: size + 2 }}>{icon}</span>
      {children}
    </span>
  );
}

// Fixed-position (viewport-relative) tooltip, not absolute - so it isn't clipped
// by an ancestor's overflow:auto/hidden (e.g. a Kanban column's scroll container).
export function ResolutionBadge({ resolutions, size = 11 }) {
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);

  if (!resolutions || resolutions.length === 0) return null;

  function computeAnchor() {
    const rect = ref.current?.getBoundingClientRect();
    return rect ? { x: rect.left + rect.width / 2, y: rect.top - 6 } : null;
  }

  return (
    <span
      ref={ref}
      onMouseEnter={() => setAnchor(computeAnchor())}
      onMouseLeave={() => setAnchor(null)}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <IconText icon="inventory_2" size={size}>{resolutions.length}</IconText>
      {anchor && (
        <div style={{
          position: 'fixed', left: anchor.x, top: anchor.y, transform: 'translate(-50%, -100%)',
          zIndex: 1000, minWidth: 160, maxWidth: 260, background: C.tin, border: `1px solid ${C.mercury}`,
          borderRadius: 6, boxShadow: '0 6px 16px rgba(34,52,105,0.18)', padding: '8px 10px',
          textAlign: 'left', pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)',
            width: 10, height: 10, background: C.tin, borderRight: `1px solid ${C.mercury}`, borderBottom: `1px solid ${C.mercury}`,
          }} />
          <div style={{ fontSize: 10, fontWeight: 600, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
            Resolved in
          </div>
          {resolutions.map((res, i) => (
            <div key={res.resolutionId ?? i} style={{ fontSize: 12, color: C.fg, display: 'flex', justifyContent: 'space-between', gap: 10, padding: '2px 0' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.productName}</span>
              <span style={{ fontFamily: 'Roboto Mono', color: C.iron, flex: 'none' }}>{res.versionLabel}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

export function ProgressBar({ value = 0, color = C.liquid, height = 4 }) {
  return (
    <div style={{ width: '100%', height, background: C.mercury, borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ width: `${value}%`, height: '100%', background: color, transition: 'width 240ms' }} />
    </div>
  );
}

export function Spinner({ size = 32, color = C.liquid, track = C.mercury }) {
  const stroke = Math.max(2, Math.round(size * 0.1));
  const r = size / 2 - stroke;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ animation: 'spin 0.9s linear infinite' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circumference} strokeDashoffset={circumference * 0.75} strokeLinecap="round" />
    </svg>
  );
}

// Standard per-page "loading data" state — a centered circular spinner with an
// optional caption, used in place of whatever ad-hoc "Loading…" text/icon each
// page previously rolled on its own.
export function PageSpinner({ label, size = 40, minHeight = 240 }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 14, minHeight, padding: 40, color: C.iron, fontSize: 13,
    }}>
      <Spinner size={size} />
      {label ? <div>{label}</div> : null}
    </div>
  );
}

export function MoneyCell({ amount, currency }) {
  const fmt = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <span style={{ fontFamily: 'Roboto Mono', fontSize: 12, color: C.fg, fontVariantNumeric: 'tabular-nums' }}>
      {currency} {fmt.format(amount)}
    </span>
  );
}
